import { useEffect, useRef, useState, type ReactNode } from 'react'

import { cn } from '../../lib/utils'
import { Button, IconButton } from './button'
import { Field, Input } from './field'

/**
 * A modal.
 *
 * `--overlay` scrim, a `line` border, 11px radius. No shadow anywhere in this product, so the
 * scrim carries the separation. Escape and the scrim both
 * close it, and focus is moved into the dialog on open so a keyboard user is not left behind on
 * the page underneath.
 */
export function Dialog({
  open,
  title,
  description,
  width = 460,
  onClose,
  children,
  footer,
}: {
  open: boolean
  title: ReactNode
  description?: ReactNode
  width?: number
  onClose: () => void
  children?: ReactNode
  footer?: ReactNode
}) {
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        aria-label="Close"
        tabIndex={-1}
        onClick={onClose}
        className="bg-overlay animate-fade absolute inset-0"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        style={{ width }}
        className="bg-panel border-line animate-fade relative max-h-full w-full overflow-y-auto rounded-[11px] border"
      >
        <header className="border-line flex items-start gap-3 border-b px-4 py-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-text text-[13.5px] font-medium">{title}</h2>
            {description && (
              <div className="text-muted mt-1 text-[12.5px] text-pretty">{description}</div>
            )}
          </div>
          <IconButton icon="close" label="Close" variant="quiet" size="sm" onClick={onClose} />
        </header>

        {children && <div className="px-4 py-4">{children}</div>}

        {footer && (
          <div className="border-line bg-panel2 flex justify-end gap-2 border-t px-4 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * A confirmation for an action that is audited, destructive, or both.
 *
 * `requireReason` is the point of it. §57 names removing a suppression as an action that must be
 * auditable, and an un-suppression with no recorded justification is exactly what an audit exists
 * to catch. The API enforces the same minimum — this is a better place to fail, not the only one.
 *
 * Three things it does that a browser `prompt()` cannot: it validates **while typing**, so the
 * work is not thrown away after the box closes; it keeps the confirm button unpressable until the
 * reason is long enough; and it puts initial focus on the field rather than on the destructive
 * button, so a stray Enter cannot complete the action.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  destructive = true,
  requireReason = false,
  reasonLabel = 'Reason',
  reasonPlaceholder,
  minReasonLength = 8,
  pending = false,
  error,
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  body?: ReactNode
  confirmLabel: string
  destructive?: boolean
  requireReason?: boolean
  reasonLabel?: string
  reasonPlaceholder?: string
  minReasonLength?: number
  pending?: boolean
  error?: string | null
  onConfirm: (reason: string) => void
  onCancel: () => void
}) {
  const [reason, setReason] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)

  // Reset on open rather than on close: leaving the previous reason in place would let one
  // justification be reused for a different record without the operator noticing.
  useEffect(() => {
    if (open) setReason('')
  }, [open])

  useEffect(() => {
    if (!open) return
    const target = requireReason ? inputRef.current : cancelRef.current
    target?.focus()
  }, [open, requireReason])

  const trimmed = reason.trim()
  const short = requireReason && trimmed.length < minReasonLength
  const canConfirm = !pending && !short

  return (
    <Dialog
      open={open}
      title={title}
      description={body}
      onClose={onCancel}
      footer={
        <>
          <Button ref={cancelRef} variant="quiet" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            disabled={!canConfirm}
            onClick={() => onConfirm(trimmed)}
          >
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </>
      }
    >
      {requireReason && (
        <Field
          label={reasonLabel}
          hint={
            short
              ? `At least ${minReasonLength} characters. This is written to the audit log.`
              : 'This is written to the audit log.'
          }
          error={error}
        >
          {(props) => (
            <Input
              {...props}
              ref={inputRef}
              value={reason}
              placeholder={reasonPlaceholder}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </Field>
      )}
      {!requireReason && error && (
        <p role="alert" className="text-bad text-[12.5px]">
          {error}
        </p>
      )}
    </Dialog>
  )
}

/**
 * The right-hand drawer.
 *
 * The design opens a record here rather than navigating away, so the list stays in place behind
 * it and an operator working down a column does not lose their position.
 */
export function Drawer({
  open,
  title,
  subtitle,
  onClose,
  actions,
  children,
  width = 560,
}: {
  open: boolean
  title: ReactNode
  subtitle?: ReactNode
  onClose: () => void
  actions?: ReactNode
  children: ReactNode
  width?: number
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button
        aria-label="Close"
        tabIndex={-1}
        onClick={onClose}
        className="bg-overlay animate-fade absolute inset-0"
      />
      <aside
        role="dialog"
        aria-modal="true"
        // `Dialog` has always done this and `Drawer` did not, so every drawer in the console was a
        // modal with no accessible name — a screen reader announced "dialog" and stopped. Found by
        // G025's browser gate, which could not locate the campaign drawer by its title.
        aria-label={typeof title === 'string' ? title : undefined}
        style={{ width }}
        className={cn(
          // No elevation anywhere in this product. The scrim behind and the left border are the
          // separation — a drawer that needs a shadow to read as on top of the page has a scrim
          // that is too weak.
          'bg-panel border-line animate-slide relative flex h-full max-w-full flex-col border-l',
        )}
      >
        <header className="border-line flex items-start gap-3 border-b px-4 py-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-text truncate text-[13.5px] font-medium">{title}</h2>
            {subtitle && <div className="text-muted mt-0.5 truncate text-[12px]">{subtitle}</div>}
          </div>
          {actions}
          <IconButton icon="close" label="Close" variant="quiet" size="sm" onClick={onClose} />
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </aside>
    </div>
  )
}
